/**
 * useMine — `GET /mine` poll contract (plan d § Files; head § Polling): debounce 2s while any row
 * is pending, back off to 10s after 60s with no transition, stop polling once nothing is pending.
 * Also polls once on `visibilitychange`/`focus` (amendment N12: "so a second tab sees the first
 * tab's takes"), and never at all while signed out and holding no take — a visitor browses and arms
 * without ever touching an authenticated route, so there is no 401 on focus. A visitor who holds a
 * free take (a house's first pick, `HouseTheme.freePick`) polls like a member: `polls`.
 *
 * `initialMine` seeds state from the route loader's server-side `/mine` read (Files table: "loader
 * = first page server-side + mine when signed in") — a signed-in visitor's first paint already
 * shows their pending/done rows with no client round-trip.
 *
 * The response IS the wall's state (review finding B1): `mergeTake` folds the row a `POST
 * /continue` just returned into the same shape the poll will return a beat later, so the column,
 * the tile and the pill move on the take itself rather than on a copy the caller kept.
 *
 * `q` (`2026_09_01_search_own_chains.md`): the caller passes `useWallPages`'s already-debounced
 * `loadedQuery`, never the raw typed value — this file adds no second debounce timer, it just
 * refetches when the settled query changes. Read through a ref (`qRef`, the same during-render-
 * assignment idiom `Wall.tsx`'s `tileByIdRef` uses) so every poll tick and every focus/visibility
 * wake sends the CURRENT query, not the one in force when the poll loop last scheduled itself.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import type {AllowanceState, Identity, MineResponse, Take} from '@core/wall/contract'

const FAST_POLL_MS = 2_000
const SLOW_POLL_MS = 10_000
const BACKOFF_AFTER_MS = 60_000

function pendingKey(mine: MineResponse | null): string {
    if (!mine) return ''
    return mine.pending.map((p) => `${p.id}:${p.status}`).join(',')
}

/**
 * `/mine` as the wall holds it: the last response, plus `byTile` — the `GET /mine?tiles=` rows
 * (`useOwnByTile`), the faces of chains beyond the response's window. Held here, not beside it, so
 * every patch below reaches every copy of a row, and a poll replaces the response but keeps them.
 */
export type MineState = MineResponse & {byTile: Take[]}

/** A reader with nothing yet: what the first take a visitor's `POST /continue` returns is folded into. */
export function emptyMine(): MineState {
    return {done: [], pending: [], pinned: [], eta_ms: null, identity: {kind: 'none'}, byTile: []}
}

/** The sign-out reset runs on the true -> false edge of `signedIn` only: a visitor's own state, held while
 *  signed out, must survive every render that follows. */
export function signedOutTransition(prevSignedIn: boolean, signedIn: boolean): boolean {
    return prevSignedIn && !signedIn
}

/** A `/mine` response taking the place of `prev`: the by-tile rows are not part of the response and
 *  outlive it. */
export function withResponse(prev: MineState | null, response: MineResponse): MineState {
    return {...response, byTile: prev?.byTile ?? []}
}

/** The row a `POST /continue` just returned, folded into `mine` - or, for a visitor with nothing yet, into an empty
 *  state, which is what starts their poll. */
export function mergeTakeInto(prev: MineState | null, take: Take): MineState {
    const mine = prev ?? emptyMine()
    const done = mine.done.filter((d) => d.id !== take.id)
    const pending = mine.pending.filter((p) => p.id !== take.id)
    const byTile = patchTake(mine.byTile, take)
    return take.status === 'done'
        ? {...mine, done: [take, ...done], pending, byTile}
        : {...mine, done, pending: [take, ...pending], byTile}
}

/**
 * Fold a row `POST /pin` just returned (plan `2026_09_04_wall_pin.md` § Client). A pin toggle
 * changes exactly one field, `pinned_at` — never `status`, never the chain's own recency — so this
 * PATCHES the row in place wherever it already sits in `done`/`pending` (a pinned take that is
 * currently its chain's face IS that chain's `done` entry) rather than routing it through
 * `withTake`'s prepend, which would wrongly promote the chain to "newest" on a pin of an OLDER take.
 * `pinned` itself is rebuilt: the old entry (if any) removed, the new one added back when still
 * pinned, resorted by `pinned_at` descending — the same order `/mine.pinned` itself carries.
 */
export function withPin(mine: MineState, take: Take): MineState {
    const patch = (rows: readonly Take[]) => patchTake(rows, take)
    const pinned = mine.pinned.filter((p) => p.id !== take.id)
    if (take.pinned_at !== null) pinned.push(take)
    pinned.sort((a, b) => (b.pinned_at ?? '').localeCompare(a.pinned_at ?? ''))
    return {
        ...mine, done: patch(mine.done), pending: patch(mine.pending), pinned, byTile: patch(mine.byTile),
        ...(mine.matched ? {matched: patch(mine.matched)} : {}),
    }
}

function patchTake(rows: readonly Take[], take: Take): Take[] {
    return rows.map((r) => (r.id === take.id ? take : r))
}

export function useMine(initialMine: MineResponse | null, signedIn: boolean, q: string = '') {
    const [mine, setMine] = useState<MineState | null>(() => (initialMine ? withResponse(null, initialMine) : null))
    /** The query the last landed response answered — `initialMine` was read with the mount's `q`.
     *  The wall takes its own matched block once, from the first answer for the query it paints. */
    const [answeredQuery, setAnsweredQuery] = useState(q)
    const lastChangeRef = useRef(Date.now())
    const lastKeyRef = useRef(pendingKey(initialMine))
    const inFlightRef = useRef(false)
    const qRef = useRef(q)
    qRef.current = q
    // Every completed attempt bumps this, and the poll effect depends on it: a fetch that fails
    // (offline, a 502 from a rolling deploy) leaves `mine` byte-identical, so without this the
    // effect would not re-run and the loop would die on the first transient error, leaving a
    // rendering tile shimmering for good (review item S1).
    const [attempt, setAttempt] = useState(0)
    /** Error-handling review F6: a 401 is not necessarily the session ending — a single stale
     *  request racing a token refresh is plausible, which is why the branch below keeps polling
     *  after the FIRST one. A SECOND consecutive 401 is not that: the loop was otherwise polling
     *  forever at `SLOW_POLL_MS`, each one silently logged to the console, with nothing on screen
     *  ever telling the reader their session had ended. `sessionExpired` stops the loop (the
     *  scheduling effect below checks it) and is read by `Wall.tsx` to raise the sign-in card
     *  once. A ref (checked synchronously at the top of `fetchMine`, before the `visibilitychange`/
     *  `focus`/`q`-change callers can fire another request) rather than state alone — those callers
     *  read `fetchMine`'s own closure, not a re-rendered `sessionExpired` value. */
    const polls = signedIn || mine !== null
    const [sessionExpired, setSessionExpired] = useState(false)
    const sessionExpiredRef = useRef(false)
    const consecutive401Ref = useRef(0)

    // Sign-out redirects to `/` with the same `Wall` mounted; `mine` was seeded once from
    // `initialMine` and the other effects only stop on `!signedIn`, so the departed reader's rows
    // stayed on screen (narcis dev, 2026-09-22). The expiry guards reset with it so the next
    // sign-in can fetch again.
    const prevSignedInRef = useRef(signedIn)
    useEffect(() => {
        const left = signedOutTransition(prevSignedInRef.current, signedIn)
        prevSignedInRef.current = signedIn
        if (!left) return
        setMine(null)
        lastKeyRef.current = pendingKey(null)
        consecutive401Ref.current = 0
        sessionExpiredRef.current = false
        setSessionExpired(false)
    }, [signedIn])

    const fetchMine = useCallback(async () => {
        if (!polls) return
        if (sessionExpiredRef.current) return
        if (inFlightRef.current) return
        inFlightRef.current = true
        // The query this attempt is sent with. Single-flight means a `q` change while it is in flight
        // is dropped at the guard above; the finally block re-fires once when that happened, so the
        // painted own-matches never settle on a query the user has already left (review finding 1).
        const sent = qRef.current
        try {
            const params = new URLSearchParams()
            if (sent) params.set('q', sent)
            const res = await fetch(`/mine${params.toString() ? `?${params}` : ''}`, {credentials: 'same-origin'})
            if (res.status === 401) {
                // Failure-logging review finding 7: this used to clear `mine` outright, so a
                // session that expired mid-poll erased the reader's whole pending set from the
                // screen with no word said. Keep the last known state instead — the same rule the
                // catch below already applies to a transient network failure — and log so a
                // persistent 401 is at least visible in the console rather than swallowed with the
                // rest of "every non-401 failure" (finding 7's own phrase for the branch below).
                console.error('[useMine] GET /mine returned 401 — session likely expired, keeping last known state')
                consecutive401Ref.current += 1
                if (consecutive401Ref.current >= 2) {
                    sessionExpiredRef.current = true
                    setSessionExpired(true)
                }
                return
            }
            if (!res.ok) {
                console.error(`[useMine] GET /mine failed with status ${res.status}`)
                return
            }
            consecutive401Ref.current = 0
            const data: MineResponse = await res.json()
            const key = pendingKey(data)
            if (key !== lastKeyRef.current) {
                lastKeyRef.current = key
                lastChangeRef.current = Date.now()
            }
            setMine((prev) => withResponse(prev, data))
            setAnsweredQuery(sent)
        } catch {
            // Transient network failure — keep the last known state; the next tick retries.
        } finally {
            inFlightRef.current = false
            setAttempt((n) => n + 1)
            if (qRef.current !== sent) void fetchMine()
        }
    }, [polls])

    /** Fold a row `POST /continue` just returned into `mine`, so the wall moves in the same frame. */
    const mergeTake = useCallback((take: Take) => {
        // A visitor's first take seeds the state (nothing to fold into yet), which is what starts the poll.
        setMine((prev) => mergeTakeInto(prev, take))
    }, [])

    /** Fold a row `POST /pin` just returned into `mine` — see `withPin`'s own doc. */
    const mergePinned = useCallback((take: Take) => {
        setMine((prev) => (prev ? withPin(prev, take) : prev))
    }, [])

    /** Add the rows a `GET /mine?tiles=` answered. Dropped while `mine` is `null` — `useOwnByTile`
     *  asks only once it is not. */
    const mergeByTile = useCallback((rows: readonly Take[]) => {
        setMine((prev) => (prev ? {...prev, byTile: [...prev.byTile, ...rows]} : prev))
    }, [])

    /** Fold an identity `POST /mine/muse` (Ovid) just returned into `mine` — the same idiom as
     *  `mergeTake`/`mergePinned`, for the one field a chosen identity touches
     *  (`IdentityPanelProps.onIdentityChange`, `@core/wall/ui/theme.ts`). A no-op while `mine` is
     *  still `null` (signed-in but the first `/mine` hasn't landed) — there is nothing to patch a
     *  field onto yet, and the next successful poll carries the true identity regardless. */
    const setIdentity = useCallback((identity: Identity) => {
        setMine((prev) => (prev ? {...prev, identity} : prev))
    }, [])

    /** Fold the allowance a `402 allowance-exhausted` just answered into `mine`: the bar says so at once, and the
     *  next `/mine` carries the server's own reading. */
    const setAllowance = useCallback((allowance: AllowanceState) => {
        setMine((prev) => (prev ? {...prev, allowance} : prev))
    }, [])

    // The poll loop: self-scheduling via setTimeout (not setInterval) so the delay can change
    // between ticks — fast while pending rows are churning, slow after a minute of no transition,
    // stopped outright once `pending` is empty.
    useEffect(() => {
        if (!polls) return
        // Error-handling review F6: an expired session must not poll forever — `fetchMine` itself
        // already refuses once `sessionExpired`, this only stops the loop from scheduling a timer
        // it knows will be a no-op.
        if (sessionExpired) return
        const hasPending = (mine?.pending.length ?? 0) > 0
        if (!hasPending) return

        const idleFor = Date.now() - lastChangeRef.current
        const delay = idleFor > BACKOFF_AFTER_MS ? SLOW_POLL_MS : FAST_POLL_MS

        const timer = setTimeout(() => {
            fetchMine()
        }, delay)
        return () => clearTimeout(timer)
    }, [mine, attempt, polls, sessionExpired, fetchMine])

    useEffect(() => {
        if (!polls) return
        function onWake() {
            if (document.visibilityState === 'visible') fetchMine()
        }
        window.addEventListener('visibilitychange', onWake)
        window.addEventListener('focus', onWake)
        return () => {
            window.removeEventListener('visibilitychange', onWake)
            window.removeEventListener('focus', onWake)
        }
    }, [fetchMine, polls])

    // `q` transitions: refetch with the new settled query. Skips the very first render when `q`
    // still names the query `initialMine` was already read with (the SSR loader passes the same
    // `q` to `readMine`, `_layout._index.tsx`) — same skip-first idiom `useWallPages` applies to its
    // own `initialPage` for the identical reason, no redundant round-trip on mount.
    const initialQRef = useRef(q)
    const skippedFirstQEffect = useRef(true)
    useEffect(() => {
        if (!polls) return
        if (skippedFirstQEffect.current) {
            skippedFirstQEffect.current = false
            if (q === initialQRef.current) return
        }
        fetchMine()
    }, [q, polls, fetchMine])

    return {mine, answeredQuery, refetchMine: fetchMine, mergeTake, mergePinned, mergeByTile, setIdentity, setAllowance, sessionExpired}
}
